(() => {
  /* Scroll suave hasta las cards de producto del catálogo.
     IMPORTANTE: este theme (Horizon) hace `html`/`body { overflow: hidden }`
     y usa `.page-wrapper { overflow-y: auto }` como contenedor de scroll real
     en desktop (>=990px, ver assets/base.css). Por eso `window.scrollTo()`
     no tenía ningún efecto visible ahí: la ventana nunca fue el elemento que
     de verdad scrollea. `scrollIntoView()` es agnóstico al contenedor —
     sube el ancestro que realmente scrollea, sea `window` o `.page-wrapper`
     — y respeta `scroll-margin-top` (bloom-exact.css) para el offset del
     header sticky, sin cálculos manuales de altura. */
  const naylumiForceScrollToProducts = () => {
    const anchor = document.getElementById('naylumi-products-anchor');
    const grid = document.querySelector('[data-naylumi-products-grid]');
    const target = anchor || grid;

    if (!target) {
      console.warn('[Naylumi] No existe #naylumi-products-anchor ni [data-naylumi-products-grid]');
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const initBloomExact = (root) => {
    if (!root || root.dataset.bloomExactInit) return;
    root.dataset.bloomExactInit = 'true';

    const burger = root.querySelector('[data-bloom-burger]');
    const mobileNav = root.querySelector('[data-bloom-mobile-nav]');
    if (burger && mobileNav) {
      burger.addEventListener('click', () => {
        mobileNav.classList.toggle('is-open');
      });
    }

    /* Categorías: aparición progresiva al entrar en viewport (una sola vez).
       Respeta prefers-reduced-motion — en ese caso no se agrega la clase que
       las oculta, así quedan visibles de inmediato sin animar. */
    const categoriesScroller = root.querySelector('.bloom-exact__categories-scroller');
    if (categoriesScroller && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      categoriesScroller.classList.add('naylumi-anim-ready');
      if ('IntersectionObserver' in window) {
        const categoriesObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              categoriesScroller.classList.add('naylumi-anim-visible');
              categoriesObserver.disconnect();
            }
          });
        }, { threshold: 0.2 });
        categoriesObserver.observe(categoriesScroller);
      } else {
        categoriesScroller.classList.add('naylumi-anim-visible');
      }
    }

    let toastTimer;
    const toast = root.querySelector('[data-bloom-toast]');
    const showToast = (message) => {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2200);
    };

    root.querySelectorAll('[data-bloom-demo-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        showToast('Agregado (demo) — conectá un producto real para vender este artículo.');
      });
    });

    root.querySelectorAll('[data-bloom-size-chip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const group = chip.closest('.bloom-card__sizes');
        group?.querySelectorAll('.bloom-card__size-chip').forEach((el) => el.classList.remove('is-active'));
        chip.classList.add('is-active');
      });
    });

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (form.closest && form.closest('.bloom-exact') && form.dataset.type === 'add-to-cart-form') {
          showToast('¡Agregado al carrito!');
        }
      },
      true
    );

    /* ---- Filtros del catálogo ---- */
    const catalog = root.querySelector('[data-bloom-catalog]');
    if (!catalog) return;

    const cards = Array.from(catalog.querySelectorAll('.bloom-card'));
    const countLabel = catalog.querySelector('[data-bloom-count]');
    const priceInput = catalog.querySelector('[data-bloom-price-range]');
    const priceLabel = catalog.querySelector('[data-bloom-price-label]');
    const emptyState = catalog.querySelector('[data-naylumi-empty-state]');

    const state = {
      status: null,
      category: 'todas',
      size: null,
      maxPrice: priceInput ? Number(priceInput.value) : Infinity,
    };

    const formatCOP = (n) => '$' + Number(n).toLocaleString('es-CO');

    // Barra móvil de filtros (creada más abajo); declaradas antes de
    // applyFilters para que esta pueda mantenerlas sincronizadas cada vez
    // que cambia cualquier filtro, sin importar desde dónde se disparó.
    const mobileFilterToggle = catalog.querySelector('[data-naylumi-filter-toggle]');
    const mobileFilterPanel = catalog.querySelector('[data-naylumi-filter-panel]');
    const mobileFilterCount = catalog.querySelector('[data-naylumi-filter-count]');
    const mobileFilterBarClear = catalog.querySelector('[data-naylumi-filter-bar-clear]');

    const updateMobileFilterBar = () => {
      let active = 0;
      if (state.category !== 'todas') active += 1;
      if (state.size) active += 1;
      if (priceInput && state.maxPrice < Number(priceInput.max)) active += 1;

      if (mobileFilterCount) {
        mobileFilterCount.textContent = String(active);
        mobileFilterCount.hidden = active === 0;
      }
      if (mobileFilterToggle) mobileFilterToggle.classList.toggle('is-active', active > 0);
      if (mobileFilterBarClear) mobileFilterBarClear.hidden = active === 0;
    };

    // Si al filtrar el usuario queda mirando un hueco vacío debajo de la
    // cuadrícula (porque ya bajó mucho y ahora hay menos productos), lo
    // regresa suavemente al inicio de la cuadrícula — no al inicio de toda
    // la página ni al hero.
    const scrollToGridIfOrphaned = () => {
      const grid = catalog.querySelector('[data-naylumi-products-grid]');
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      if (rect.bottom < 80) {
        const anchor = document.getElementById('naylumi-products-anchor') || grid;
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // La primera llamada ocurre al cargar la página (estado inicial, sin
    // interacción del usuario); a partir de la segunda, cada llamada viene
    // de un click real en algún filtro, así que recién ahí tiene sentido
    // revisar si hay que reubicar el scroll.
    let hasAppliedOnce = false;

    const applyFilters = () => {
      let visible = 0;
      cards.forEach((card) => {
        const status = card.dataset.status || '';
        const category = card.dataset.category || '';
        const price = Number(card.dataset.price || 0);
        const sizes = (card.dataset.sizes || '').split(',').filter(Boolean);

        let show = true;
        if (state.status && status !== state.status) show = false;
        if (state.category !== 'todas' && category !== state.category) show = false;
        if (state.size && !sizes.includes(state.size)) show = false;
        if (price > state.maxPrice) show = false;

        // is-hidden se aplica en .bloom-card-wrap (la celda real del grid
        // — ver assets/bloom-exact.css), no en .bloom-card: .bloom-card-wrap
        // es hijo directo de .bloom-exact__grid con height:100%, así que
        // ocultar solo .bloom-card (adentro) dejaba el wrap completo
        // ocupando su celda vacío — el hueco reportado al filtrar.
        (card.closest('.bloom-card-wrap') || card).classList.toggle('is-hidden', !show);
        if (show) visible += 1;
      });
      if (countLabel) countLabel.textContent = `${visible} producto${visible === 1 ? '' : 's'} disponible${visible === 1 ? '' : 's'}`;
      if (emptyState) {
        emptyState.hidden = visible !== 0;
        emptyState.classList.toggle('is-visible', visible === 0);
      }
      updateMobileFilterBar();
      if (hasAppliedOnce) scrollToGridIfOrphaned();
      hasAppliedOnce = true;
    };

    const toggleChipGroup = (selector, stateKey, allValue) => {
      catalog.querySelectorAll(selector).forEach((chip) => {
        chip.addEventListener('click', () => {
          const value = chip.dataset.value;
          catalog.querySelectorAll(selector).forEach((el) => el.classList.remove('is-active'));

          if (value === allValue || state[stateKey] === value) {
            state[stateKey] = allValue || null;
            const fallback = catalog.querySelector(`${selector}[data-value="${allValue}"]`);
            fallback?.classList.add('is-active');
          } else {
            state[stateKey] = value;
            chip.classList.add('is-active');
          }
          applyFilters();
        });
      });
    };

    toggleChipGroup('[data-bloom-filter="category"]', 'category', 'todas');

    catalog.querySelectorAll('[data-bloom-filter="status"]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const value = chip.dataset.value;
        const isActive = chip.classList.contains('is-active');
        catalog.querySelectorAll('[data-bloom-filter="status"]').forEach((el) => el.classList.remove('is-active'));
        if (isActive) {
          state.status = null;
        } else {
          state.status = value;
          chip.classList.add('is-active');
        }
        applyFilters();
      });
    });

    catalog.querySelectorAll('[data-bloom-filter="size"]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const value = chip.dataset.value;
        const isActive = chip.classList.contains('is-active');
        catalog.querySelectorAll('[data-bloom-filter="size"]').forEach((el) => el.classList.remove('is-active'));
        if (isActive) {
          state.size = null;
        } else {
          state.size = value;
          chip.classList.add('is-active');
        }
        applyFilters();
      });
    });

    if (priceInput) {
      priceInput.addEventListener('input', () => {
        state.maxPrice = Number(priceInput.value);
        if (priceLabel) priceLabel.textContent = formatCOP(state.maxPrice);
        applyFilters();
      });
    }

    // Select de "Ordenar por": existe dos veces en el HTML (uno dentro del
    // panel para desktop, otro compacto en la barra sticky para móvil), pero
    // solo uno de los dos es visible/interactivo según el viewport (el otro
    // queda display:none por CSS). En vez de sincronizar sus valores entre
    // sí, ambos disparan la misma función — no hay estado duplicado que
    // mantener alineado.
    const sortSelect = catalog.querySelector('[data-bloom-sort]');
    const mobileSortSelect = catalog.querySelector('[data-bloom-sort-mobile]');

    const applySortValue = (value) => {
      // El order de flex/grid solo funciona en el hijo directo del
      // contenedor grid (.bloom-exact__grid > .bloom-card-wrap) — .bloom-card
      // vive un nivel adentro del wrap, así que asignarle order a él no
      // reordenaba nada visualmente. Se asigna en .bloom-card-wrap.
      if (value === 'price-asc' || value === 'price-desc') {
        const sorted = [...cards].sort((a, b) => {
          const priceA = Number(a.dataset.price || 0);
          const priceB = Number(b.dataset.price || 0);
          return value === 'price-asc' ? priceA - priceB : priceB - priceA;
        });
        sorted.forEach((card, index) => {
          (card.closest('.bloom-card-wrap') || card).style.order = String(index);
        });
      } else {
        cards.forEach((card) => {
          (card.closest('.bloom-card-wrap') || card).style.order = '';
        });
      }
    };

    if (sortSelect) {
      sortSelect.addEventListener('change', () => applySortValue(sortSelect.value));
    }
    if (mobileSortSelect) {
      mobileSortSelect.addEventListener('change', () => applySortValue(mobileSortSelect.value));
    }

    const clearBtn = catalog.querySelector('[data-bloom-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        state.status = null;
        state.category = 'todas';
        state.size = null;
        state.maxPrice = priceInput ? Number(priceInput.max) : Infinity;
        catalog.querySelectorAll('.bloom-exact__chip, .bloom-exact__size-chip').forEach((el) => {
          el.classList.remove('is-active');
        });
        catalog.querySelector('[data-bloom-filter="category"][data-value="todas"]')?.classList.add('is-active');
        if (priceInput) {
          priceInput.value = priceInput.max;
          if (priceLabel) priceLabel.textContent = formatCOP(priceInput.max);
        }
        if (sortSelect) sortSelect.value = 'relevancia';
        if (mobileSortSelect) mobileSortSelect.value = 'relevancia';
        cards.forEach((card) => {
          (card.closest('.bloom-card-wrap') || card).style.order = '';
        });
        applyFilters();
      });
    }

    // El botón "Limpiar filtros" del empty state (0 resultados) reutiliza
    // el mismo clearBtn de arriba — sin duplicar la lógica de reseteo.
    const clearEmptyBtn = emptyState?.querySelector('[data-naylumi-clear-empty]');
    if (clearEmptyBtn && clearBtn) {
      clearEmptyBtn.addEventListener('click', () => clearBtn.click());
    }

    // Barra sticky móvil: el botón "Filtrar" abre/cierra el acordeón con
    // categorías, tallas, precio y limpiar. "Ver resultados" y el botón
    // rápido "Limpiar" de la barra reutilizan el mismo panel/botón, sin
    // lógica de filtrado duplicada.
    if (mobileFilterToggle && mobileFilterPanel) {
      // El panel ya no es position:fixed (ver bloom-exact.css — vivía pegado
      // al viewport con un `top` en px calculado una sola vez acá, así que
      // quedaba encima del hero si el usuario scrolleaba después de abrirlo).
      // Ahora fluye normal dentro de .naylumi-filters-sticky-shell (el mismo
      // padre sticky de la barra), así que no necesita ningún cálculo de
      // posición en JS — solo togglear la clase.
      const setPanelOpen = (open) => {
        mobileFilterPanel.classList.toggle('is-open', open);
        mobileFilterToggle.setAttribute('aria-expanded', String(open));
      };

      mobileFilterToggle.addEventListener('click', () => {
        setPanelOpen(!mobileFilterPanel.classList.contains('is-open'));
      });

      const closeBtn = mobileFilterPanel.querySelector('[data-naylumi-filter-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => setPanelOpen(false));
      }

      // Cerrar al tocar fuera del panel y del botón que lo abre. Este bloque
      // corre una sola vez (initBloomExact está guardado por sección), así
      // que no hay riesgo de listeners duplicados.
      document.addEventListener('click', (event) => {
        if (!mobileFilterPanel.classList.contains('is-open')) return;
        const clickedInsidePanel = mobileFilterPanel.contains(event.target);
        const clickedToggle = mobileFilterToggle.contains(event.target);
        if (!clickedInsidePanel && !clickedToggle) {
          setPanelOpen(false);
        }
      });

      // Red de seguridad adicional (más allá de que el sticky ya se "suelta"
      // solo al salir del catálogo): si el catálogo deja de estar
      // realmente a la vista — para cualquier lado, subiendo al hero o
      // bajando al footer — se cierra el panel de verdad (clase +
      // aria-expanded), no solo visualmente. Así no queda "abierto" a
      // medias si el usuario vuelve a entrar al catálogo con scroll.
      // rootMargin negativo en vez de threshold:0: el catálogo mide miles
      // de px (todo el grid de productos), así que con el scroll llevado
      // al extremo superior de la página siempre queda un borde suyo
      // asomando al fondo del viewport — nunca llega a "0% visible" ahí,
      // así que threshold:0 nunca disparaba. Encoger el área de
      // intersección 80px arriba/abajo hace que cuente como "afuera" en
      // cuanto queda poco menos que un asomo del catálogo, no 100% nada.
      if ('IntersectionObserver' in window) {
        const catalogVisibilityObserver = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (entry && !entry.isIntersecting && mobileFilterPanel.classList.contains('is-open')) {
              setPanelOpen(false);
            }
          },
          { threshold: 0, rootMargin: '-80px 0px -80px 0px' }
        );
        catalogVisibilityObserver.observe(catalog);
      }
    }

    if (mobileFilterBarClear && clearBtn) {
      mobileFilterBarClear.addEventListener('click', () => clearBtn.click());
    }

    applyFilters();
  };

  document.querySelectorAll('.bloom-exact').forEach(initBloomExact);
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target.matches && target.matches('.bloom-exact')) initBloomExact(target);
    target.querySelectorAll('.bloom-exact').forEach(initBloomExact);
  });

  /* Categorías circulares -> filtro del catálogo + scroll a productos.
     Listener delegado en document (fase de captura) en vez de uno por
     elemento: así sigue funcionando aunque Shopify reinyecte HTML de
     secciones en Customize, y captura el click antes que cualquier otro
     handler en la cadena. bloom-exact.js se incluye una vez por sección
     (13+ veces en el home), así que esta única versión definitiva se
     registra una sola vez con una bandera global, evitando disparar el
     filtro/scroll varias veces por click. */
  if (!window.__naylumiCategoryScrollBound) {
    window.__naylumiCategoryScrollBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const categoryLink = event.target.closest('[data-naylumi-category]');
        if (!categoryLink) return;

        const category = categoryLink.getAttribute('data-naylumi-category');
        if (!category) return;

        const filterButton =
          document.querySelector(`[data-bloom-filter="category"][data-value="${category}"]`) ||
          document.querySelector(`[data-naylumi-filter="${category}"]`) ||
          document.querySelector(`[data-filter="${category}"]`) ||
          document.querySelector(`[data-category="${category}"]`);

        event.preventDefault();
        event.stopPropagation();

        if (filterButton) {
          filterButton.click();
        } else {
          console.warn('[Naylumi] No se encontró filtro para categoría:', category);
        }

        // Doble intento: el primero baja rápido, el segundo corrige si el
        // filtro reacomoda el grid o el navegador tarda en repintar.
        window.setTimeout(naylumiForceScrollToProducts, 220);
        window.setTimeout(naylumiForceScrollToProducts, 520);
      },
      true
    );
  }

  /* Offset real para la barra sticky de filtros del catálogo (móvil).
     El header nativo de Horizon (#header-group) está oculto por CSS
     (display:none !important) en este theme, y la barra de anuncios
     (.bloom-exact__announce) no es sticky — solo .bloom-exact__header lo
     es. Por eso el único offset que importa es la altura real de ese
     header, medida en vivo (no un número fijo estimado) y actualizada si
     cambia (p. ej. el logo termina de cargar, o cambia el texto/idioma).
     Se guarda en --naylumi-mobile-header-h, la misma variable que ya
     consume .naylumi-mobile-filter-bar en bloom-exact.css. */
  if (!window.__naylumiHeaderOffsetBound) {
    window.__naylumiHeaderOffsetBound = true;

    const headerEl = document.querySelector('.bloom-exact__header');
    if (headerEl) {
      const applyHeaderOffset = () => {
        const height = Math.round(headerEl.getBoundingClientRect().height);
        if (height > 0) {
          document.documentElement.style.setProperty('--naylumi-mobile-header-h', `${height}px`);
        }
      };

      applyHeaderOffset();

      if (window.ResizeObserver) {
        new ResizeObserver(applyHeaderOffset).observe(headerEl);
      } else {
        window.addEventListener('resize', applyHeaderOffset);
      }
    }
  }

})();

/* Naylumi filtros fijos — MÓVIL: DESACTIVADO (auditoría Cline,
   2026-07-27). Este IIFE fijaba la barra compacta con position:fixed
   dinámico (top/left/width calculados a mano) y usaba histéresis
   intencional (releaseLimit = topOffset+barHeight+80, refixLimit =
   topOffset+barHeight+140) para evitar vibración al fijar/soltar — pero
   esa misma histéresis es la causa confirmada de "tarda en pegarse" (la
   barra debía pasar 140px de más antes de fijarse) y "se queda pegado
   al volver arriba" (con scroll rápido hacia arriba, productsBottom podía
   no llegar a releaseLimit a tiempo). Se reemplaza por position:sticky
   CSS puro en .naylumi-mobile-filter-bar (ver assets/bloom-exact.css) —
   sin JS, sin delay, sin estado que pueda quedar atascado. La lógica de
   abrir/cerrar el panel de filtros, aplicar/ordenar/limpiar NO vive acá
   (ver el manejador de [data-naylumi-filter-toggle] más abajo en este
   archivo) y sigue intacta. */

/* Naylumi filtros fijos — ESCRITORIO: DESACTIVADO (bug urgente).
   Este bloque fijaba el panel de filtros con position:fixed calculando
   su posición vía getBoundingClientRect()/offsetTop, pero en desktop
   (≥990px) Horizon usa `.page-wrapper` como contenedor de scroll real
   (html/body quedan con overflow:hidden — ver assets/base.css) en vez
   de `window`. Mezclar "fixed relativo al viewport" con "el scroll
   ocurre dentro de un div, no en window" es una combinación frágil: se
   reportó el filtro quedando "atrapado dentro de un fondo/overlay" con
   una barra de scroll interna al bajar la página. En vez de parchear la
   geometría a ciegas sin poder ver el render en vivo, se elimina el
   fijado y el panel queda siempre en su posición normal del documento
   (scroll normal del navegador / .page-wrapper, sin position:fixed
   propio). El acordeón móvil (.naylumi-filters-card.is-open, otro
   mecanismo, más abajo en este archivo) no se toca. Las clases CSS que
   este bloque usaba (.is-naylumi-desktop-fixed,
   .naylumi-desktop-filter-placeholder) también se quitaron de
   assets/bloom-exact.css. */

(function () {
  // Mini "pop" de confirmación al agregar a favoritos. No cambia la lógica
  // real de favoritos (eso vive en naylumi-favorites.js) — solo observa el
  // estado ya aplicado (.is-active) después del clic y agrega una clase
  // CSS temporal para animar el pop.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.bloom-card__fav');
    if (!btn) return;

    setTimeout(() => {
      if (btn.classList.contains('is-active')) {
        btn.classList.add('naylumi-heart-added');
        setTimeout(() => btn.classList.remove('naylumi-heart-added'), 620);
      }
    }, 80);
  });
})();

(function () {
  // Idea 10 — aparición suave al hacer scroll (cards del catálogo, cards de
  // "También te puede gustar" e títulos de sección con línea inferior).
  // Este archivo se incluye una vez por sección (13+ veces en el home), así
  // que este bloque se registra una sola vez con una bandera global.
  if (window.__naylumiRevealBound) return;
  window.__naylumiRevealBound = true;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

  // Marca global: la CSS solo oculta contenido antes de revelarlo cuando
  // este atributo está presente. Si este script no llega a correr, nunca
  // se agrega y todo queda visible por defecto (sin depender de JS).
  document.documentElement.setAttribute('data-naylumi-reveal-ready', 'true');

  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('naylumi-anim-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  const revealSelector = '.bloom-card, .bloom-exact__h2, .product-recommendations .resource-list__item';

  document.querySelectorAll(revealSelector).forEach((el) => revealObserver.observe(el));

  // Las cards de "También te puede gustar" se inyectan después por JS
  // propio del tema (fetch de recomendaciones), no existen al cargar la
  // página — se observan en cuanto aparecen en el DOM.
  const recoSection = document.querySelector('.product-recommendations');
  if (recoSection && 'MutationObserver' in window) {
    const recoMutationObserver = new MutationObserver(() => {
      recoSection.querySelectorAll('.resource-list__item:not(.naylumi-anim-visible)').forEach((el) => {
        revealObserver.observe(el);
      });
    });
    recoMutationObserver.observe(recoSection, { childList: true, subtree: true });
  }

  // Secciones reinyectadas por el editor de temas.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(revealSelector).forEach((el) => revealObserver.observe(el));
  });
})();

/* ============================================================
   Fix de rendimiento (auditoría real con Playwright/Chromium,
   naylumi-performance-audit.js): causa confirmada del freeze en
   scroll = capas decorativas de fondo (watercolor/silk/mist/
   mariposas/estrellas/golddust) animando sin pausa todo el tiempo.
   Las 3 bloques de abajo solo agregan/quitan clases CSS — la pausa
   real (animation-play-state:paused) vive en bloom-exact.css. Ningún
   bloque lee layout (nada de getBoundingClientRect/offsetTop) ni
   recorre las cards del catálogo — no tocan filtros, favoritos,
   imágenes ni producto.
   ============================================================ */

/* 1) Pausa mientras el usuario hace scroll activamente (cualquier
   parte de la página). Listener passive, sin lectura de layout —
   solo classList + un timeout para "soltar" 180ms después de que el
   scroll se detiene, y requestAnimationFrame para no apilar trabajo
   si hay muchos eventos de scroll seguidos.

   bloom-exact.js se incluye una vez por sección (13+ veces en el
   home) — sin esta bandera global, este bloque (y los dos de abajo)
   se ejecutarían 13 veces, cada uno con su propio scrollTimer/
   IntersectionObserver duplicado sobre los mismos elementos (mismo
   patrón de guard que ya usa el reveal-on-scroll más arriba en este
   archivo, window.__naylumiRevealBound).

   Bug real detectado con Playwright/Chromium real (no solo lectura de
   código): en escritorio (≥990px) Horizon deja html/body con
   overflow:hidden y usa .page-wrapper como contenedor de scroll real
   (ver comentarios de assets/base.css) — window.scrollY se queda en 0
   todo el tiempo ahí, así que un listener solo en "window" nunca
   dispara en desktop. Se escucha también en .page-wrapper (si existe)
   para cubrir ambos casos sin adivinar cuál aplica. */
if (!window.__naylumiPauseDecorOnScrollBound) {
  window.__naylumiPauseDecorOnScrollBound = true;

  (function naylumiPauseDecorOnScroll() {
    const root = document.documentElement;
    const scrollContainers = [window];
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) scrollContainers.push(pageWrapper);

    let scrollTimer = null;
    let ticking = false;

    function markScrolling() {
      root.classList.add('naylumi-is-scrolling');

      if (scrollTimer) {
        window.clearTimeout(scrollTimer);
      }

      scrollTimer = window.setTimeout(() => {
        root.classList.remove('naylumi-is-scrolling');
      }, 180);
    }

    function onScroll() {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        markScrolling();
      });
    }

    scrollContainers.forEach((el) => {
      el.addEventListener('scroll', onScroll, { passive: true });
    });
  })();
}

/* 2) Pausa mientras el catálogo de productos está en pantalla — la
   auditoría confirmó que el freeze se siente específicamente al
   pasar por productos. Selector real: [data-bloom-catalog] es la
   <section> completa del catálogo (sections/naylumi-catalog.liquid),
   con fallback a #naylumi-products-anchor y .bloom-exact__grid si el
   primero no existiera en algún template. */
if (!window.__naylumiPauseDecorInCatalogBound) {
  window.__naylumiPauseDecorInCatalogBound = true;

  (function naylumiPauseDecorInCatalog() {
    const root = document.documentElement;
    const catalog =
      document.querySelector('[data-bloom-catalog]') ||
      document.querySelector('#naylumi-products-anchor') ||
      document.querySelector('.bloom-exact__grid');

    if (!catalog || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            root.classList.add('naylumi-catalog-active');
          } else {
            root.classList.remove('naylumi-catalog-active');
          }
        });
      },
      {
        root: null,
        threshold: 0.08,
        rootMargin: '120px 0px 120px 0px',
      }
    );

    observer.observe(catalog);
  })();
}

/* 3) Pausa cada capa decorativa individualmente cuando ESA capa en
   particular sale de viewport (redundante a propósito con 1 y 2: los
   decorativos de este theme son position:absolute dentro de su
   sección — no fixed — así que si alguno queda fuera de las 2 clases
   de arriba por estar en una sección intermedia, esto lo cubre
   igual). */
if (!window.__naylumiPauseDecorOutOfViewBound) {
  window.__naylumiPauseDecorOutOfViewBound = true;

  (function naylumiPauseDecorOutOfView() {
    if (!('IntersectionObserver' in window)) return;

    const decor = document.querySelectorAll(
      [
        '.naylumi-bg-watercolor',
        '.naylumi-bg-silk',
        '.naylumi-bg-mist',
        '.naylumi-bg-butterflies',
        '.naylumi-bg-stars',
        '.naylumi-bg-golddust',
      ].join(',')
    );

    if (!decor.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('is-naylumi-bg-paused');
          } else {
            entry.target.classList.add('is-naylumi-bg-paused');
          }
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '240px 0px 240px 0px',
      }
    );

    decor.forEach((el) => observer.observe(el));
  })();
}
